export const THEME_PREFERENCE_KEY = 'alberto:themePreference'

export type ThemePreference = 'system' | 'light' | 'dark'
export type EffectiveTheme = 'light' | 'dark'

const THEME_MEDIA_QUERY = '(prefers-color-scheme: dark)'

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function loadThemePreference(): ThemePreference {
  try {
    const storedValue = localStorage.getItem(THEME_PREFERENCE_KEY)
    if (isThemePreference(storedValue)) {
      return storedValue
    }
  } catch {
    return 'system'
  }

  return 'system'
}

export function saveThemePreference(nextPreference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_PREFERENCE_KEY, nextPreference)
  } catch {
    // Ignore storage write failures so preference updates do not block the UI.
  }
}

export function getSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }

  return window.matchMedia(THEME_MEDIA_QUERY).matches ? 'dark' : 'light'
}

export function resolveEffectiveTheme(themePreference: ThemePreference): EffectiveTheme {
  if (themePreference === 'system') {
    return getSystemTheme()
  }

  return themePreference
}

export function applyThemeToDocument(theme: EffectiveTheme): void {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

export function watchSystemThemeChanges(onChange: (theme: EffectiveTheme) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined
  }

  const mediaQuery = window.matchMedia(THEME_MEDIA_QUERY)
  const listener = (event: MediaQueryListEvent) => {
    onChange(event.matches ? 'dark' : 'light')
  }

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', listener)
    return () => {
      mediaQuery.removeEventListener('change', listener)
    }
  }

  mediaQuery.addListener(listener)
  return () => {
    mediaQuery.removeListener(listener)
  }
}
